import { useEffect, useState } from "react";
import { Trash2, Mail, MailOpen } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getContacts, markContactRead, deleteContact } from "@/lib/api";
import { formatDateTime } from "@/lib/dateUtils";

const formatDate = (dateStr) => formatDateTime(dateStr);

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchContacts = async () => {
    try {
      const response = await getContacts();
      setContacts(response.data);
    } catch (error) {
      console.error("Fout bij het ophalen van contacten:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleView = async (contact) => {
    setSelectedContact(contact);
    setDialogOpen(true);
    
    if (!contact.is_read) {
      try {
        await markContactRead(contact.id);
        fetchContacts();
      } catch (error) {
        console.error("Fout bij het markeren als gelezen:", error);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Weet je zeker dat je dit bericht wilt verwijderen?")) return;

    try {
      await deleteContact(id);
      toast.success("Bericht succesvol verwijderd");
      fetchContacts();
    } catch (error) {
      toast.error("Fout bij het verwijderen van het bericht");
      console.error("Fout bij het verwijderen van het bericht:", error);
    }
  };

  const unreadCount = contacts.filter(c => !c.is_read).length;

  return (
    <div data-testid="admin-contacts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900 font-['Chivo'] tracking-tight">Berichten</h1>
          <p className="text-slate-500 font-['Manrope'] mt-1 flex items-center gap-2">
            Contactformulier inzendingen
            {unreadCount > 0 && (
              <Badge className="bg-rose-100 text-rose-600 hover:bg-rose-100">{unreadCount} ongelezen</Badge>
            )}
          </p>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead className="text-slate-500 font-semibold font-['Manrope'] uppercase text-xs tracking-wider">Van</TableHead>
                  <TableHead className="text-slate-500 font-semibold font-['Manrope'] uppercase text-xs tracking-wider">Onderwerp</TableHead>
                  <TableHead className="text-slate-500 font-semibold font-['Manrope'] uppercase text-xs tracking-wider">Datum</TableHead>
                  <TableHead className="text-right text-slate-500 font-semibold font-['Manrope'] uppercase text-xs tracking-wider">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">Laden...</TableCell>
                  </TableRow>
                ) : contacts.length > 0 ? (
                  contacts.map((contact) => (
                    <TableRow 
                      key={contact.id} 
                      className={`cursor-pointer hover:bg-slate-50 transition-colors ${!contact.is_read ? "bg-blue-50" : ""}`}
                      onClick={() => handleView(contact)}
                      data-testid={`contact-row-${contact.id}`}
                    >
                      <TableCell>
                        {contact.is_read ? (
                          <MailOpen className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Mail className="h-4 w-4 text-blue-600" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className={`${!contact.is_read ? "font-bold" : "font-medium"}`}>
                            {contact.name}
                          </p>
                          <p className="text-sm text-slate-500">{contact.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className={!contact.is_read ? "font-semibold" : ""}>
                        {contact.subject}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {formatDate(contact.created_at)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(contact.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      Nog geen berichten ontvangen
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Message Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] font-bold text-slate-900">Bericht Details</DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 font-['Manrope']">Van</p>
                  <p className="font-medium text-slate-900">{selectedContact.name}</p>
                </div>
                <div>
                  <p className="text-slate-500 font-['Manrope']">Email</p>
                  <p className="font-medium text-slate-900">{selectedContact.email}</p>
                </div>
              </div>
              <div>
                <p className="text-slate-500 text-sm font-['Manrope']">Onderwerp</p>
                <p className="font-medium text-slate-900">{selectedContact.subject}</p>
              </div>
              <div>
                <p className="text-slate-500 text-sm font-['Manrope']">Bericht</p>
                <div className="mt-2 p-4 bg-slate-50 rounded-xl whitespace-pre-wrap text-slate-700">
                  {selectedContact.message}
                </div>
              </div>
              <div className="text-right text-sm text-slate-500 font-['Manrope']">
                Ontvangen: {formatDate(selectedContact.created_at)}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-['Manrope']">
                  Sluiten
                </Button>
                <Button 
                  asChild 
                  className="bg-blue-600 hover:bg-blue-700 font-['Manrope']"
                >
                  <a href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}>
                    Beantwoorden via Email
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
